import React, { useCallback, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { OtherLoginType } from './index.type'
import type { OtherItem } from './index.type'
import CustomButton from '@/components/CustomButton'

const GOOGLE_CLIENT_ID = '324747988873-u5s1sr9jk8om1he320o4mt1klh09grca.apps.googleusercontent.com'

interface OtherLoginItemsProps {
  onGoogleLogin?: (idToken: string) => void
}

export default React.memo(function OtherLoginItems({ onGoogleLogin }: OtherLoginItemsProps) {
  const { t } = useTranslation()
  const popupRef = useRef<Window | null>(null)

  const otherItems = React.useMemo<OtherItem[]>(
    () => ([
      { type: OtherLoginType.Google, icon: '/icon/google-icon.svg', text: t('Google快捷登录') },
      // { type: OtherLoginType.Apple, icon: '/icon/apple-icon.svg', text: t('Apple 快捷登录') }
    ]),
    [t]
  )

  // 监听 popup 回调消息
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return
      if (event.data?.type !== 'GOOGLE_LOGIN_CALLBACK') return

      const { idToken, error } = event.data
      if (idToken && onGoogleLogin) {
        onGoogleLogin(idToken)
      } else if (error) {
        console.error('Google OAuth error:', error)
      }

      // 确保弹窗关闭
      if (popupRef.current && !popupRef.current.closed) {
        popupRef.current.close()
      }
      popupRef.current = null
    }

    window.addEventListener('message', handleMessage)
    return () => window.removeEventListener('message', handleMessage)
  }, [onGoogleLogin])

  const handleGoogleLogin = useCallback(() => {
    const redirectUri = `${window.location.origin}/login/google-callback`
    const params = new URLSearchParams({
      client_id: GOOGLE_CLIENT_ID,
      redirect_uri: redirectUri,
      response_type: 'id_token',
      scope: 'openid email profile',
      nonce: `zise_${Date.now()}`,
      prompt: 'select_account',
    })

    // 计算弹窗居中位置
    const width = 500
    const height = 600
    const left = window.screenX + (window.outerWidth - width) / 2
    const top = window.screenY + (window.outerHeight - height) / 2

    popupRef.current = window.open(
      `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`,
      'google-login',
      `width=${width},height=${height},left=${left},top=${top},scrollbars=yes`
    )
  }, [])

  const handleClick = useCallback((type: OtherLoginType) => {
    if (type === OtherLoginType.Google) {
      handleGoogleLogin()
    }
  }, [handleGoogleLogin])

  const OtherLoginItemsElements = React.useMemo(
    () => otherItems.map(({ type, icon, text }) => {
      return (
        <CustomButton key={type} color="default" onClick={() => handleClick(type)}>
          <div className="flex items-center justify-center space-x-2">
            <img src={icon} alt={text} />
            <span>{text}</span>
          </div>
        </CustomButton>
      )
    }),
    [otherItems, handleClick]
  )

  return (
    <div className="mt-6">
      <p className="text-center text-gray-500 text-sm mb-4">{t('或使用以下方式登录')}</p>
      <div className="space-y-3">{OtherLoginItemsElements}</div>
    </div>
  )
})
